import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPackages, fetchServices, fetchSettings, fetchFaq } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { Truck, ShieldCheck, FileText, ClipboardList, Check, ArrowRight, Sparkles, Wrench, MessageCircle, Plus } from "lucide-react";
import { useCart, isIncluded } from "@/lib/cart";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getPackageStyle } from "@/lib/package-styles";
import { pageMeta, jsonLdScriptTag, professionalServiceJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/")({
  head: () => {
    const m = pageMeta({
      path: "/",
      title: "Truck Company Center | LLC, USDOT y MC en Florida",
      description:
        "LLC en Florida, EIN, USDOT, MC, BOL, W-9, Central Dispatch y Super Dispatch para nuevos carriers y owner-operators.",
      ogTitle: "Truck Company Center — Trucking setup para car carriers",
      ogDescription:
        "LLC, EIN, USDOT, MC, BOL, W-9, Central Dispatch y Super Dispatch. Proceso claro para nuevos carriers.",
    });
    return {
      meta: m.meta,
      links: m.links,
      scripts: [
        {
          type: "application/ld+json",
          children: jsonLdScriptTag(professionalServiceJsonLd()),
        },
      ],
    };
  },
  component: Home,
});

function Home() {
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings });
  const { data: faq = [] } = useQuery({ queryKey: ["faq"], queryFn: fetchFaq });
  const hero = settings?.hero ?? {};
  const popularExtras = services.filter((s) => ["logo","tarjetas-negocio","clase-central","bol","central-dispatch","super-dispatch"].includes(s.slug));
  return (
    <div>
      <section className="relative overflow-hidden border-b border-hairline bg-radial-fade">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-16 md:px-6 md:pb-28 md:pt-24">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-label-accent">
            <span className="inline-block h-px w-8 bg-secondary/60" /> Florida · Trucking setup
          </div>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.05] text-foreground md:text-6xl">
            {hero.headline ?? "Te montamos tu compañía desde cero"}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{hero.subheadline ?? "Te ayudamos con LLC, EIN, USDOT/MC si aplica, logo, Gmail, Central Dispatch, Super Dispatch y más. Todo en un proceso claro, profesional y sin improvisación."}</p>
          <div className="mt-8 flex min-h-[7.5rem] flex-wrap items-center gap-3 sm:min-h-[3.25rem]">
            <Link to="/paquetes"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">Ver paquetes <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/servicios"><Button size="lg" variant="outline" className="border-hairline bg-surface hover:bg-surface-2">Elegir lo que necesito</Button></Link>
            {settings?.contact?.whatsapp_url && (
              <a href={settings.contact.whatsapp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <MessageCircle className="h-4 w-4" /> Hablar por WhatsApp
              </a>
            )}
          </div>
          <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-hairline bg-surface/60 px-3 py-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-secondary" />
            {hero.trust_note ?? "Sin improvisación. Sin vueltas. Todo estructurado."}
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { icon: FileText, label: "Trámites", value: "Crear Compañía, EIN, DOT, MC" },
              { icon: ShieldCheck, label: "Cumplimiento", value: "USDOT/MC y BOC-3" },
              { icon: Truck, label: "Plataformas", value: "Central Dispatch + Super Dispatch" },
              { icon: ClipboardList, label: "Documentos", value: "BOL y W-9" },
            ].map((c) => (
              <div key={c.label} className="rounded-md border border-hairline bg-surface p-3">
                <c.icon className="h-4 w-4 text-primary" />
                <div className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                <div className="text-sm font-medium text-foreground">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-2 md:gap-16 md:px-6 md:py-24">
          <div className="md:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-label-accent">El problema</div>
            <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">Empezar solo es caos. Y caro.</h2>
            <ul className="mt-6 space-y-3 text-muted-foreground">
              {["No saber por dónde empezar.","No entender qué trámite va antes y cuál va después.","Perder tiempo entre plataformas, documentos, fees y seguros.","Información mezclada en grupos, videos y comentarios."].map((t) => (
                <li key={t} className="flex gap-3"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive/80" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-hairline bg-surface p-6 md:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-primary">Nuestra solución</div>
            <h3 className="mt-3 font-display text-2xl font-semibold text-foreground md:text-3xl">Un solo proceso, todo en orden.</h3>
            <ul className="mt-6 space-y-3 text-foreground">
              {["Eliges paquete o pides servicios por separado.","Revisas tu pedido y pagas de forma segura.","Llenas un formulario claro con tus datos.","Empezamos los trámites y te mantenemos al tanto."].map((t) => (
                <li key={t} className="flex gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Paquetes</div>
              <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">Elige cómo quieres empezar</h2>
            </div>
            <Link to="/paquetes" className="text-sm text-muted-foreground hover:text-foreground">Ver todo →</Link>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((p) => (<PackageCard key={p.id} pkg={p} />))}
          </div>
        </div>
      </section>

      <section className="border-b border-hairline">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-label-accent">A tu medida</div>
              <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">¿No quieres paquete? Elige lo que necesitas</h2>
              <p className="mt-3 max-w-xl text-muted-foreground">Pide solo los servicios que necesitas. Si tu carrito se acerca a un paquete, te avisamos cuánto ahorrarías.</p>
            </div>
            <Link to="/servicios"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90"><Wrench className="mr-2 h-4 w-4" /> Ver servicios</Button></Link>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline bg-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Proceso</div>
          <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">Así funciona</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-4">
            {[{n:"01",t:"Eliges paquete o servicios",d:"Empieza con un paquete o pide los servicios que necesitas por separado."},{n:"02",t:"Revisas tu pedido",d:"Pagas de forma segura. Te confirmamos por WhatsApp o email."},{n:"03",t:"Llenas tu formulario",d:"Recogemos los datos clave de tu compañía."},{n:"04",t:"Empezamos tu proceso",d:"Hacemos los trámites con orden y te mantenemos al tanto."}].map((s) => (
              <div key={s.n} className="rounded-md border border-hairline bg-surface p-5">
                <div className="text-xs font-mono tracking-widest text-primary">{s.n}</div>
                <div className="mt-3 font-medium text-foreground">{s.t}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {popularExtras.length > 0 && (
        <section className="border-b border-hairline">
          <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Extras</div>
                <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">Más populares</h2>
              </div>
              <Link to="/servicios" className="text-sm text-muted-foreground hover:text-foreground">Ver todos →</Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {popularExtras.map((s) => <ExtraCard key={s.id} s={s} />)}
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-hairline">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Dudas</div>
          <h2 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">Preguntas frecuentes</h2>
          <Accordion type="single" collapsible className="mt-8">
            {faq.slice(0, 5).map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-hairline">
                <AccordionTrigger className="text-left text-foreground">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-6 text-center"><Link to="/faq" className="text-sm text-primary hover:underline">Ver todas las preguntas →</Link></div>
        </div>
      </section>

      <section className="bg-radial-fade">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <div className="rounded-xl border border-hairline bg-surface p-8 text-center md:p-14">
            <Sparkles className="mx-auto h-6 w-6 text-primary" />
            <h2 className="mt-4 font-display text-2xl font-semibold text-foreground md:text-4xl">Si quieres entrar serio a esta industria, empieza con una estructura seria.</h2>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link to="/paquetes"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">Empezar ahora</Button></Link>
              <Link to="/servicios"><Button size="lg" variant="outline" className="border-hairline bg-surface hover:bg-surface-2">Elegir mi setup</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PackageCard({ pkg }: { pkg: any }) {
  const addPackage = useCart((s) => s.addPackage);
  const style = getPackageStyle(pkg.slug);
  return (
    <div className={`relative flex flex-col rounded-xl border bg-surface p-6 transition ${style.cardBorder}`}>
      {style.ribbon && (
        <div className={`absolute -top-3 left-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${style.ribbon.class}`}>
          {style.ribbon.label}
        </div>
      )}
      <div className="flex flex-col md:min-h-[14rem]">
        <div className={`text-xs uppercase tracking-wider ${style.badgeText}`}>Paquete</div>
        <h3 className="mt-1 font-display text-xl font-semibold text-foreground">{pkg.name}</h3>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-3xl font-semibold text-foreground">{money(pkg.price)}</span>
          <span className="text-sm text-muted-foreground line-through">{money(pkg.original_value)}</span>
        </div>
        <div className="mt-1 inline-flex w-fit rounded bg-success/15 px-2 py-0.5 text-[11px] font-medium text-[oklch(0.78_0.16_155)]">Ahorras {money(pkg.savings)}</div>
        <p className="mt-4 text-sm text-muted-foreground">{pkg.description}</p>
      </div>
      <Button className={`mt-6 ${style.buttonClass}`}
        onClick={() => addPackage({ id: pkg.id, slug: pkg.slug, name: pkg.name, price: Number(pkg.price), includedServiceIds: [...(pkg.service_ids ?? []), ...(pkg.bonus_service_ids ?? [])] })}>
        Elegir {pkg.name}
      </Button>
      <Link to="/paquetes" className="mt-3 text-center text-xs text-muted-foreground hover:text-foreground">Ver detalles</Link>
    </div>
  );
}

function ExtraCard({ s }: { s: any }) {
  const addService = useCart((st) => st.addService);
  const items = useCart((st) => st.items);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const inCart = mounted && items.some((it) => it.type === "service" && it.id === s.id);
  const included = mounted && isIncluded(s.id, items);
  return (
    <div className="flex flex-col rounded-md border border-hairline bg-surface p-4">
      <div className="text-[11px] uppercase tracking-wider text-label-accent">{s.category}</div>
      <div className="mt-1 font-medium text-foreground">{s.name}</div>
      <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.description}</p>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-display text-lg font-semibold text-foreground">{money(s.price)}</span>
        {included ? (
          <Button size="sm" variant="outline" disabled className="border-hairline opacity-60">Incluido</Button>
        ) : (
          <Button size="sm" disabled={inCart}
            className={inCart ? "border border-hairline bg-transparent text-foreground opacity-80" : "bg-primary text-primary-foreground hover:bg-primary/90"}
            onClick={() => addService({ id: s.id, slug: s.slug, name: s.name, price: Number(s.price) })}>
            {inCart ? "Añadido ✓" : <><Plus className="mr-1 h-3 w-3" />Añadir</>}
          </Button>
        )}
      </div>
    </div>
  );
}
